import { z } from 'zod';
import { AdsId } from './ads-builder.js';
/**
 * Контракты «Дублировать кампанию» (`POST /api/ads/campaigns/:campaignId/duplicate`,
 * план 2026-11-02, A2b): admin-api вызывает K2 auto-ads
 * (`/internal/meta-builder/campaigns/:campaignId/duplicate`).
 *
 * Тело — `.strict()`. Ответы мягкие: тексты `message` / `hint` / `why` от auto-ads
 * ПО-АНГЛИЙСКИ (для модели), экран переводит их по КОДУ и ПОЛЮ словарём и сырой
 * английский не показывает.
 */
export const ADS_DUPLICATE_NAME_MAX = 120;
export const AdsCampaignDuplicateRequest = z
    .object({
    platformAccountId: AdsId,
    /** Брать и выключенные ad set / объявления (по умолчанию — только включённые). */
    includePaused: z.boolean().optional(),
    /** Имя копии; кириллицу сервер транслитерирует, `#ml-` дописывает сам. */
    name: z
        .string()
        .trim()
        .min(1)
        .max(ADS_DUPLICATE_NAME_MAX)
        .refine((v) => !/[{}]/.test(v), { message: 'В имени нельзя использовать { и }' })
        .optional(),
})
    .strict();
const Text = z.string().max(2000);
export const AdsDuplicateBlocker = z.object({
    code: z.string().min(1).max(100),
    message: Text.catch(''),
});
export const AdsDuplicateFidelity = z
    .object({
    carried: z.array(z.object({ field: z.string(), value: Text.catch('') })).catch([]),
    changed: z
        .array(z.object({
        field: z.string(),
        from: Text.catch(''),
        to: Text.catch(''),
        why: Text.catch(''),
    }))
        .catch([]),
    notCarried: z
        .array(z.object({
        code: z.string().min(1).max(100),
        field: z.string().nullish().catch(null),
        hint: Text.catch(''),
    }))
        .catch([]),
    rules: z
        .array(z.object({
        ruleId: z.string(),
        name: z.string().max(300).catch(''),
        originalBinding: z.string().catch('included'),
        copyGets: Text.catch(''),
        /** Машинные поля (K2b); у старого ответа нет — экран тогда читает `copyGets`. */
        coverage: z.enum(['yes', 'maybe', 'no']).optional().catch(undefined),
        via: z.enum(['all', 'name', 'id']).optional().catch(undefined),
        mayEnableCopy: z.boolean().optional().catch(undefined),
        /** A/B-вариант бюджета, к которому привязан оригинал; у старого ответа нет. */
        abVariantKey: z.string().optional().catch(undefined),
    }))
        .catch([]),
})
    .catch({ carried: [], changed: [], notCarried: [], rules: [] });
const nullableText = z
    .string()
    .nullish()
    .catch(null)
    .transform((v) => v ?? null);
/**
 * Происхождение копии (POST 201 и GET `duplicates/:id` отдают одно и то же). Поля до K2b у старых
 * копий — null; незнакомые значения приводятся к null, а не роняют ответ.
 */
export const AdsDuplicateOrigin = z.object({
    kind: z.string(),
    campaignId: z.string(),
    platformAccountId: z.string(),
    campaignName: z.string().catch(''),
    /** Брались ли выключенные группы и объявления; null — неизвестно (старая копия). */
    includePaused: z
        .boolean()
        .nullish()
        .catch(null)
        .transform((v) => v ?? null),
    /** Сколько выключенных в оригинале групп и объявлений попало в копию. */
    pausedChildren: z
        .object({
        adSets: z.number().int().nonnegative().catch(0),
        ads: z.number().int().nonnegative().catch(0),
    })
        .nullish()
        .catch(null)
        .transform((v) => v ?? null),
    requestedName: nullableText,
    /** Кто подготовил: админка или нейросеть (агент). */
    source: z
        .enum(['admin', 'agent'])
        .nullish()
        .catch(null)
        .transform((v) => v ?? null),
    createdBy: nullableText,
    preparedAt: nullableText,
    copiedFrom: nullableText,
});
const AdsDuplicateStructure = z
    .object({
    adSets: z.number().int().nonnegative(),
    ads: z.number().int().nonnegative(),
    groups: z.array(z.string()).catch([]),
})
    .nullish()
    .catch(null)
    .transform((v) => v ?? null);
const AdsDuplicateOriginField = AdsDuplicateOrigin.nullish()
    .catch(null)
    .transform((v) => v ?? null);
export const AdsCampaignDuplicateResponse = z.object({
    templateId: AdsId,
    templateName: z.string().catch(''),
    origin: AdsDuplicateOriginField,
    /** Готовый вход проверки пачки (`mode: draft`, `activation: manual`) — разбирает экран. */
    batchInput: z.record(z.string(), z.unknown()),
    structure: AdsDuplicateStructure,
    fidelity: AdsDuplicateFidelity,
    blockers: z.array(AdsDuplicateBlocker).catch([]),
});
/** Пачка, уже созданная из копии: `execute` = запущена на выполнение (не «завершена»). */
export const AdsDuplicateBatchRef = z.object({
    batchId: AdsId,
    status: z.string().catch(''),
    createdAt: z.string().catch(''),
});
/**
 * `GET /api/ads/duplicates/:templateId` — копия, открытая с сервера (K2b): то, что сохранил POST,
 * `batchInput` пересобран под ТЕКУЩУЮ версию шаблона, ключ пачки выдаёт сервер.
 * `legacy: true` — копия подготовлена до K2b (постов и бюджетов групп нет, fidelity — null).
 */
export const AdsDuplicateView = z.object({
    templateId: AdsId,
    templateName: z.string().catch(''),
    templateVersion: z.number().int().catch(0),
    origin: AdsDuplicateOriginField,
    batchInput: z.record(z.string(), z.unknown()),
    structure: AdsDuplicateStructure,
    /** null — копия старая (legacy): блока «Что перенеслось» нет. */
    fidelity: z.union([z.null(), AdsDuplicateFidelity]),
    /** Новые сверху (порядок сервера не гарантируем — экран сортирует по `createdAt`). */
    batches: z.array(AdsDuplicateBatchRef).catch([]),
    legacy: z.boolean().catch(false),
});
/** 422 `duplicate-blocked`: `error.details` = блокеры и fidelity (шаблон не создан). */
export const AdsDuplicateBlockedDetails = z.object({
    blockers: z.array(AdsDuplicateBlocker).min(1),
    fidelity: AdsDuplicateFidelity,
});
//# sourceMappingURL=ads-duplicate.js.map